// SWR hooks — all GETs go through here; mutations use apiJson directly.

import useSWR, { type SWRResponse } from "swr";

import { apiJson } from "./client";
import type {
  AnalyzeStatus,
  ListCounts,
  ListItemsResponse,
  ListName,
  QueueStatsResponse,
  TitleDetail,
} from "./types";

function fetcher<T>(url: string): Promise<T> {
  return apiJson<T>(url);
}

/** Per-list counts for the sidebar tree roots. */
export function useLists(): SWRResponse<ListCounts, Error> {
  return useSWR<ListCounts>("/api/lists", fetcher<ListCounts>);
}

/** Items of one list. Pass `null` to skip (collapsed tree node). */
export function useListItems(name: ListName | null): SWRResponse<ListItemsResponse, Error> {
  return useSWR<ListItemsResponse>(
    name == null ? null : `/api/lists/${name}`,
    fetcher<ListItemsResponse>,
  );
}

/** Full title detail. Pass `null` to skip (nothing selected). */
export function useTitle(imdbId: string | null): SWRResponse<TitleDetail, Error> {
  return useSWR<TitleDetail>(
    imdbId == null ? null : `/api/titles/${imdbId}`,
    fetcher<TitleDetail>,
  );
}

/** Per-batch queue statistics (the statistics view). */
export function useQueueStats(): SWRResponse<QueueStatsResponse, Error> {
  return useSWR<QueueStatsResponse>("/api/stats/queues", fetcher<QueueStatsResponse>);
}

/** Analyze job status. Auto-polls every 1.5 s while running; quiet otherwise. */
export function useAnalyzeStatus(): SWRResponse<AnalyzeStatus, Error> {
  return useSWR<AnalyzeStatus>("/api/analyze/status", fetcher<AnalyzeStatus>, {
    refreshInterval: (latest) => (latest?.state === "running" ? 1500 : 0),
    revalidateOnFocus: false,
  });
}
